
import {
  computed,
  defineComponent,
  PropType,
  onMounted,
  reactive,
  toRefs,
  onUnmounted,
  ref
} from 'vue'

import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import ListHeader from '@/components/ListHeader.vue'
import { Entry, EntryConfig, RequestHandler, UUID } from '@Protium/vue-ozone-utils'
import { GLOBALS, ItemViewMode, ItemViewModeDict, STATUS } from '@/constants'
import Directr from '@/services/directr'
import TableListView from './TableListView.vue'

interface EntryColumnConfig {
  itemSize?: string
}

enum ListEvent {
  add = 'add',
  active = 'active',
  remove = 'remove',
  select = 'select',
  search = 'search'
}

export enum ColumnView {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
  Table = 'table'
}

export default defineComponent({
  name: 'EntryColumn',
  components: { ListHeader, TableListView },
  props: {
    name: {
      type: String,
      default: null
    },
    minWidth: {
      type: [String, Number],
      default: 380
    },
    maxWidth: {
      type: [String, Number],
      default: 640
    },
    width: {
      type: String
    },
    classList: {
      type: [String, Array],
      default: ''
    },
    type: {
      type: String
    },
    view: {
      type: String as PropType<ColumnView>,
      default: ColumnView.Vertical
    },
    items: {
      type: Array as PropType<Array<Entry>>,
      required: true
    },
    config: {
      type: Object as PropType<EntryConfig>
    },
    itemView: {
      type: Number as PropType<ItemViewMode>,
      default: ItemViewMode.Default
    },
    itemConfig: {
      type: Object
    },
    paginate: {
      type: Number,
      default: 0
    },
    request: {
      type: Object as PropType<RequestHandler<Entry>>
    },
    selection: {
      type: Array as PropType<Array<Entry>>,
      default: () => ([])
    },
    expandable: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['active', 'select', 'add', 'remove', 'search', 'resolved', 'expand', 'update:modelValue'],
  setup (props, { emit }) {
    const $store = useStore()
    const $directr = $store.getters['Services/Directr']
    const container = ref()

    const elements = ref<Array<Element>>([])
    const expanded = ref<Array<string>>([])
    const warned = ref<Array<string>>([])
    const cache = ref<Array<Entry>>([]) // start empty so any item triggers a full load

    const pageState = reactive({
      current: 1,
      show: computed(() => props.paginate),
      total: computed(() => Math.ceil(props.items?.length / (props.paginate || props.items?.length))),
      offset: computed(() => Math.max(0, pageState.current - 1) * props.paginate),
      items: computed(() => props.paginate <= 0 ? props.items : props.items.slice(pageState.offset, Number(pageState.offset) + Number(props.paginate)))
    })


    onMounted(() => {
      window.addEventListener('keyup', (e) => checkKeyInput(e))
    })

    onUnmounted(() => {
      window.removeEventListener('keyup', (e) => checkKeyInput(e))
    })

    function checkKeyInput (e) {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        goToNextElement(e.key)
      }
    }

    function goToNextElement (key: 'ArrowRight' | 'ArrowLeft') {
      const activeItem = expanded.value
      if (activeItem.length !== 0) {
        const findItem: any = props.items.find(x => x.sys.id === (activeItem[0]))
        const index: number = props.items.indexOf(findItem)

        if (!((key === 'ArrowRight' && index === props.items.length - 1) || (key === 'ArrowLeft' && index === 0))) {
          if (key === 'ArrowRight') void toggleExpandItem(props.items[index + 1])
          else void toggleExpandItem(props.items[index - 1])
        }
      }
    }

    function dragFrom (event) {
      // console.log('Drag start', event)
    }

    // @Prototype - proto for more generic resolver leveraging cache first
    async function resolveWithCache (id: UUID, request: Directr = $directr): Promise<Entry | undefined> {
      // console.log('resolveWithCache', id)

      // check already loaded items list first...
      let item = cache.value.find(p => p.sys.id === id)

      // if (item) console.log('> item fetched from cache', id)
      // if nothing, query api
      if (!item) {
        // console.log('< resolving item...', id)
        const { items } = await request.get('id', [id])
        item = (items || [])[0]
        if (item) {
          cache.value.push(item)
          emit('resolved', item) // so that the parent can update it's store
        }
      }

      return item
    }

    // resolve the clicked item
    // COMBAK this is very roundabout since we can already know the clicked element beforehand and thus trigger element.setProp that way
    async function toggleExpandItem (item: Entry, setProp?, event?) {
      if (props.view === 'horizontal' && expanded.value.length === 1) expanded.value.splice(0, 1)
      // console.log('toggle', item)
      if (!item || (item && !item.sys)) return
      const index = expanded.value.findIndex(e => e === item.sys.id)
      const value = typeof event === 'boolean' ? event : index >= 0 ? false : true
      // console.log(item.sys.id, index, value, typeof setProp === 'function' ? 'use setProp' : '', (value !== true && index >= 0) ? 'splice' : '')

      if (value !== true && index >= 0) expanded.value.splice(index, 1)
      else if (value !== false && index < 0) expanded.value.push(item.sys.id)

      if (!item.sys?.id) {
        console.warn('toggleExpandItem | item had no sys.id and cannot be resolved...')
        return
      }
      // resolve product id and retrieve variant ids
      await resolveWithCache(item.sys.id).catch(e => console.error('resolveWithCache error', e))
      if (typeof setProp === 'function') setProp('expand', value)
      if (props.view === 'horizontal') setHorizontalScroll(item.sys.id)
      emit('active', item)
    }

    function setHorizontalScroll (itemId) {
      const element: any = document.getElementById('horizontal-scrol-list')
      const findItem: any = props.items.find(x => x.sys.id === itemId)
      const index = props.items.indexOf(findItem)

      if (element) {
        const xScrollPos = element?.children[index].offsetLeft
        element.scrollTo({ behavior: 'smooth', left: xScrollPos - 25 })
        // the -25 is the padding/margin on the left of the first element
      }
    }

    const columnClassList = computed(() => ({
      [`column-size--${ItemViewModeDict[props.itemView]}`]: true
    }))

    return {
      elements,
      goToNextElement,
      setHorizontalScroll,
      container,
      requestHandler: props.request || $store.getters['Services/Directr'],
      contentType: computed(() => props.type || ((props.items || [])[0].sys).type || 'unknown'),
      subtitle: computed(() => `${props.items.length} ${props.type}s`),
      emit: (type: ListEvent, $event) => emit(type, $event),
      itemSelection: computed(() => props.selection.map(n => n?.sys?.id).filter(f => !!f)),
      ColumnView,
      STATUS,
      dragFrom,
      cache,
      expanded,
      warned,
      toggleExpandItem,
      columnClassList,
      pageState
    }
  }
})
